import { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Input } from '../ui/input';
import {
  Zap, TrendingUp, TrendingDown, DollarSign, ShoppingCart,
  ArrowUpRight, ArrowDownRight, Banknote, CreditCard, ArrowRightLeft,
  Calendar, AlertTriangle, Trash2, Package
} from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

export default function FlashReport({ branchId, token, t, lang, onDataReady }) {
  const [data, setData] = useState(null);
  const [date, setDate] = useState(new Date().toISOString().split('T')[0]);
  const [loading, setLoading] = useState(true);
  const fmt = (v) => Number(v || 0).toLocaleString(lang === 'ar' ? 'ar-SA' : 'en-SA', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

  useEffect(() => {
    const fetch = async () => {
      setLoading(true);
      try {
        const { data: d } = await axios.get(`${API}/api/accounting/flash-report?branch_id=${branchId || ''}&date=${date}`, { headers: { Authorization: `Bearer ${token}` } });
        setData(d);
        if (onDataReady) onDataReady(d);
      } catch { toast.error(t('common.failed')); }
      setLoading(false);
    };
    fetch();
  }, [branchId, token, date, t, lang, onDataReady]);

  if (loading) return <div className="flex items-center justify-center py-20"><div className="w-8 h-8 border-2 border-amber-500 border-t-transparent rounded-full animate-spin" /></div>;
  if (!data) return null;

  const compCards = [
    { label: t('flash.vsYesterday'), value: data.vs_yesterday, ref: fmt(data.yesterday_revenue) },
    { label: t('flash.vsLastWeek'), value: data.vs_last_week, ref: fmt(data.last_week_revenue) },
  ];

  return (
    <div className="space-y-5" data-testid="flash-report-tab">
      {/* Date Picker */}
      <div className="flex items-center gap-3">
        <Calendar className="w-4 h-4 text-muted-foreground" />
        <Input type="date" value={date} onChange={e => setDate(e.target.value)} className="w-44" data-testid="flash-date-picker" />
        <Badge variant="outline" className="text-xs">{data.orders} {t('flash.orders')}</Badge>
      </div>

      {/* Main Metrics */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        {[
          { label: t('accounting.totalRevenue'), value: fmt(data.revenue), icon: TrendingUp, color: 'from-emerald-500 to-green-600', textColor: 'text-emerald-500' },
          { label: t('accounting.totalExpenses'), value: fmt(data.expenses), icon: TrendingDown, color: 'from-red-500 to-rose-600', textColor: 'text-red-500' },
          { label: t('accounting.netProfit'), value: fmt(data.net_profit), icon: DollarSign, color: data.net_profit >= 0 ? 'from-amber-500 to-orange-600' : 'from-red-500 to-rose-600', textColor: data.net_profit >= 0 ? 'text-amber-500' : 'text-red-500' },
          { label: t('flash.avgOrder'), value: fmt(data.avg_order), icon: ShoppingCart, color: 'from-blue-500 to-indigo-600', textColor: 'text-blue-500' },
        ].map((c, i) => (
          <Card key={i} className="relative overflow-hidden" data-testid={`flash-metric-${i}`}>
            <CardContent className="p-4">
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-xs text-muted-foreground">{c.label}</p>
                  <p className={`text-xl font-bold mt-1 ${c.textColor}`}>{c.value} <span className="text-xs font-normal text-muted-foreground">{t('common.sar')}</span></p>
                </div>
                <div className={`w-9 h-9 rounded-lg bg-gradient-to-br ${c.color} flex items-center justify-center`}>
                  <c.icon className="w-4 h-4 text-white" />
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Comparison Cards */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
        {compCards.map((c, i) => {
          const isUp = c.value > 0;
          return (
            <Card key={i} className={`border ${isUp ? 'border-emerald-500/20' : c.value < 0 ? 'border-red-500/20' : 'border-border'}`}>
              <CardContent className="p-4 flex items-center justify-between">
                <div>
                  <p className="text-xs text-muted-foreground">{c.label}</p>
                  <p className="text-sm text-muted-foreground mt-0.5">{c.ref} {t('common.sar')}</p>
                </div>
                <div className={`flex items-center gap-1 text-sm font-bold ${isUp ? 'text-emerald-500' : c.value < 0 ? 'text-red-500' : 'text-muted-foreground'}`}>
                  {isUp ? <ArrowUpRight className="w-4 h-4" /> : c.value < 0 ? <ArrowDownRight className="w-4 h-4" /> : null}
                  {c.value > 0 ? '+' : ''}{c.value}%
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Bottom Row: Top Items + Payment + Waste + Cash */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Top 5 Items */}
        <Card data-testid="flash-top-items">
          <CardContent className="p-4">
            <h3 className="font-semibold text-foreground text-sm mb-3">{t('flash.topItems')}</h3>
            {data.top_items.length === 0 ? (
              <p className="text-xs text-muted-foreground text-center py-4">{t('flash.noSales')}</p>
            ) : (
              <div className="space-y-2">
                {data.top_items.map((item, i) => (
                  <div key={i} className="flex items-center justify-between text-sm">
                    <div className="flex items-center gap-2">
                      <span className="w-5 h-5 rounded-full bg-amber-500/10 text-amber-600 flex items-center justify-center text-xs font-bold">{i + 1}</span>
                      <span className="text-foreground">{item.name}</span>
                    </div>
                    <div className="flex items-center gap-3 text-xs text-muted-foreground">
                      <span>{item.qty}x</span>
                      <span className="font-medium text-foreground">{fmt(item.revenue)}</span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Revenue by Payment + Cash Status + Waste */}
        <div className="space-y-3">
          {/* Payment Methods */}
          <Card data-testid="flash-payments">
            <CardContent className="p-4">
              <h3 className="font-semibold text-foreground text-sm mb-2">{t('accounting.revenueByPayment')}</h3>
              <div className="flex gap-3">
                {[
                  { key: 'cash', icon: Banknote, label: t('accounting.cash'), color: 'text-emerald-500' },
                  { key: 'card', icon: CreditCard, label: t('accounting.card'), color: 'text-blue-500' },
                  { key: 'transfer', icon: ArrowRightLeft, label: t('accounting.transfer'), color: 'text-purple-500' },
                ].map(pm => {
                  const val = data.revenue_by_payment[pm.key];
                  return (
                    <div key={pm.key} className="flex-1 text-center p-2 rounded-lg bg-muted/30">
                      <pm.icon className={`w-4 h-4 mx-auto ${pm.color}`} />
                      <p className="text-xs text-muted-foreground mt-1">{pm.label}</p>
                      <p className="text-sm font-bold">{val ? fmt(val.total) : '0'}</p>
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>

          {/* Cash + Waste Row */}
          <div className="grid grid-cols-2 gap-3">
            <Card data-testid="flash-cash">
              <CardContent className="p-3">
                <div className="flex items-center gap-2 mb-1">
                  <Banknote className="w-4 h-4 text-amber-500" />
                  <span className="text-xs font-medium">{t('flash.cashStatus')}</span>
                </div>
                {data.cash_status.closed_shifts > 0 ? (
                  <p className={`text-sm font-bold ${data.cash_status.difference === 0 ? 'text-emerald-500' : 'text-red-500'}`}>
                    {data.cash_status.difference === 0 ? t('flash.balanced') : `${fmt(data.cash_status.difference)} ${t('common.sar')}`}
                  </p>
                ) : (
                  <p className="text-xs text-muted-foreground">{data.cash_status.open_shifts > 0 ? t('flash.shiftsOpen') : t('flash.noShifts')}</p>
                )}
              </CardContent>
            </Card>
            <Card data-testid="flash-waste">
              <CardContent className="p-3">
                <div className="flex items-center gap-2 mb-1">
                  <Trash2 className="w-4 h-4 text-red-500" />
                  <span className="text-xs font-medium">{t('flash.waste')}</span>
                </div>
                <p className="text-sm font-bold text-red-500">{fmt(data.waste.total_cost)} <span className="text-xs font-normal text-muted-foreground">{t('common.sar')}</span></p>
                <p className="text-[10px] text-muted-foreground">{data.waste.count} {t('flash.entries')}</p>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>

      {/* Profit Margin Badge */}
      <Card className={`border ${data.net_profit >= 0 ? 'border-emerald-500/20 bg-emerald-500/5' : 'border-red-500/20 bg-red-500/5'}`}>
        <CardContent className="p-4 text-center">
          <p className="text-sm text-muted-foreground">{t('flash.profitMargin')}</p>
          <p className={`text-3xl font-bold ${data.net_profit >= 0 ? 'text-emerald-500' : 'text-red-500'}`}>{data.profit_margin}%</p>
        </CardContent>
      </Card>
    </div>
  );
}
